/**
 * Global data prefetch store.
 * Fetches critical data for ALL galaxies on app startup so navigation is instant.
 * Components use usePrefetchOrFetch() to get data without duplicate requests.
 */
import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── External store (no React re-render cascades) ──
let store = {};
let storeListeners = new Set();
let bootstrapStarted = false;  // module-level: ensure bootstrap fires once across provider remounts

function getSnapshot() { return store; }
function subscribe(listener) {
  storeListeners.add(listener);
  return () => storeListeners.delete(listener);
}
function setStoreField(key, value) {
  store = { ...store, [key]: value };
  storeListeners.forEach(fn => fn());
}

/** Raw store access — components re-render when store changes. */
export function usePrefetchedData() {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Smart hook: returns data from prefetch store if available,
 * otherwise fetches after a grace period. No duplicate requests.
 * 
 * @param {string} storeKey - Key in the prefetch store
 * @param {string} url - API URL to fetch if not in store
 * @param {object} opts - { method: "get"|"post", delay: ms before fallback }
 * @returns {[data, loading]}
 */
export function usePrefetchOrFetch(storeKey, url, opts = {}) {
  const { method = "get", delay = 1500, validate } = opts;
  const prefetched = usePrefetchedData();
  const isValid = (v) => v != null && (typeof validate !== "function" || validate(v));
  const [data, setData] = useState(() => (isValid(prefetched[storeKey]) ? prefetched[storeKey] : null));
  const [loading, setLoading] = useState(!isValid(prefetched[storeKey]));
  const fetchedRef = useRef(false);

  // Sync from store when valid data arrives
  useEffect(() => {
    if (isValid(prefetched[storeKey]) && !data) {
      setData(prefetched[storeKey]);
      setLoading(false);
      fetchedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetched, storeKey, data]);

  // Delayed fallback: fetch live if store never delivered valid data
  useEffect(() => {
    if (fetchedRef.current || data) return;
    const timer = setTimeout(() => {
      if (fetchedRef.current) return;
      fetchedRef.current = true;
      const req = method === "post" ? axios.post(url) : axios.get(url);
      req.then(r => {
        if (!isValid(r.data)) return;
        setData(prev => prev || r.data);
        setStoreField(storeKey, r.data); // Share with store
      }).catch(() => {}).finally(() => setLoading(false));
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey, url, method, delay, data]);

  return [data, loading];
}

// ── Provider: hydrates store from /api/bootstrap, then fills gaps ──
export const DataPrefetchProvider = ({ children }) => {
  useEffect(() => {
    if (bootstrapStarted) return;  // module-level guard — only fire once per page load
    bootstrapStarted = true;

    const fire = (key, url, method) => {
      const req = method === "post" ? axios.post(url) : axios.get(url);
      req.then(r => setStoreField(key, r.data)).catch(() => {});
    };

    // Step 1: ONE HTTP call hydrates ~30 store keys (galaxy/research/policy/aicci/...)
    axios.get(`${API}/bootstrap`)
      .then(r => {
        const data = r.data?.data || {};
        Object.entries(data).forEach(([key, value]) => setStoreField(key, value));
      })
      .catch(() => {})
      .finally(() => {
        // Step 2: Fill the remaining keys not covered by bootstrap (or refresh stale)
        const missing = (key) => !store[key];
        if (missing("galaxyOverview")) fire("galaxyOverview", `${API}/galaxy/overview`);
        if (missing("pulse")) fire("pulse", `${API}/pulse`);
        if (missing("researchStats")) fire("researchStats", `${API}/research/stats`);
        if (missing("researchCii")) fire("researchCii", `${API}/research/cii`);
        if (missing("companyStats")) fire("companyStats", `${API}/companies/stats`);
        if (missing("companyIndustryMap")) fire("companyIndustryMap", `${API}/companies/industry-map`);
        if (missing("policyStats")) fire("policyStats", `${API}/policy/stats`);
        if (missing("policyList")) fire("policyList", `${API}/policy/list?limit=400`);
        if (missing("hwOverview")) fire("hwOverview", `${API}/anatomy/devices/overview`);
        if (missing("hwStats")) fire("hwStats", `${API}/galaxy/stats?entity_type=device`);
        if (missing("aicciApp")) fire("aicciApp", `${API}/aicci?entity_type=application`);
        if (missing("aicciDevice")) fire("aicciDevice", `${API}/aicci?entity_type=device`);
        if (missing("aicciGlobal")) fire("aicciGlobal", `${API}/aicci`);

        // Step 3: Secondary data (delayed 500ms to keep the main thread free)
        setTimeout(() => {
          if (missing("researchGap")) fire("researchGap", `${API}/research/gap-analysis`, "post");
          if (missing("companyLeaderboard")) fire("companyLeaderboard", `${API}/companies/leaderboard?limit=20`);
          if (missing("swInclusion")) fire("swInclusion", `${API}/galaxy/inclusion?entity_type=application`);
          if (missing("hwInclusion")) fire("hwInclusion", `${API}/galaxy/inclusion?entity_type=device`);
          if (missing("researchFields")) fire("researchFields", `${API}/research/fields`);
          if (missing("researchPulse")) fire("researchPulse", `${API}/research/pulse`);
          if (missing("hwNewThisWeek")) fire("hwNewThisWeek", `${API}/mirror/new-this-week`);
          if (missing("hwSources")) fire("hwSources", `${API}/galaxy/sources?entity_type=device`);
          if (missing("researchTrendsRadar")) fire("researchTrendsRadar", `${API}/research/trends-radar`);
          if (missing("researchIndustryGap")) fire("researchIndustryGap", `${API}/research/industry-gap`);
          if (missing("equityScenarios")) fire("equityScenarios", `${API}/equity/scenarios`);
          if (missing("researchSourceCmp")) fire("researchSourceCmp", `${API}/research/source-comparison`);
          if (missing("geoIntelligence")) fire("geoIntelligence", `${API}/research/geo-intelligence`);
          if (missing("policyInfluence")) fire("policyInfluence", `${API}/research/policy-influence`);
          if (missing("discoveryInsights")) fire("discoveryInsights", `${API}/discovery-insights`);
          if (missing("biasAudit")) fire("biasAudit", `${API}/bias-audit`);
          if (missing("mirrorTrends")) fire("mirrorTrends", `${API}/mirror/trends`);
        }, 500);

        // Step 4: Cross-galaxy idle prefetch (1.5s after main thread quiets) —
        // warms data the user is likely to hit when they navigate away from the
        // Consciousness dashboard. Bootstrap should cover most of these; this
        // is a fallback for when bootstrap is missing entries.
        const idleFire = (cb) => {
          if (typeof window !== "undefined" && "requestIdleCallback" in window) {
            window.requestIdleCallback(cb, { timeout: 3000 });
          } else {
            setTimeout(cb, 1500);
          }
        };
        idleFire(() => {
          if (missing("industryFuturesMatrix")) fire("industryFuturesMatrix", `${API}/industry/futures/matrix`);
          if (missing("industryTrends")) fire("industryTrends", `${API}/industry/trends`);
          if (missing("companySolutionsStats")) fire("companySolutionsStats", `${API}/companies/solutions-stats`);
          if (missing("companySolutionsProductsDefault")) fire("companySolutionsProductsDefault", `${API}/companies/solutions-products?limit=100`);
          if (missing("appsStatsQuick")) fire("appsStatsQuick", `${API}/stats/quick`);
        });
      });
  }, []);

  return children;
};
